import { resolveThemeColors, ROLE_NAMES, type RoleName, type ThemeMode } from "@app/core";
import { cn, RotateCcw } from "@app/ui";
import { useMemo, useState } from "react";
import { useI18n } from "../../../lib/useI18n.js";
import { boardTargetKey, usePaletteBoardStore } from "../../../stores/index.js";
import { useThemeStore } from "../../../stores/theme.store.js";
import { humanizeRole as humanize } from "./board-targets.js";

/** Editor for the generated chrome roles — drill into any single generated color
 *  and override it per light/dark; Revert restores the generated value.
 *  Clicking a row arms it as the palette board's paint target. */
export function RolePaletteEditor() {
  const { t } = useI18n();
  const config = useThemeStore((s) => s.config);
  const setRoleOverride = useThemeStore((s) => s.setRoleOverride);
  const armedTarget = usePaletteBoardStore((s) => s.target);
  const arm = usePaletteBoardStore((s) => s.arm);
  const [mode, setMode] = useState<ThemeMode>("light");
  const armedKey = armedTarget ? boardTargetKey(armedTarget) : null;

  // Material scheme generation costs a few ms × 34 roles — don't re-run it on
  // unrelated re-renders (this component re-renders on EVERY theme mutation,
  // including per-pointermove board drags).
  const generated = useMemo(
    () => resolveThemeColors({ ...config, roleOverrides: { light: {}, dark: {} } }, mode),
    [config.source, config.sourceDark, config.variant, mode],
  );
  const overrides = config.roleOverrides[mode];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1 self-start rounded-md border p-1">
        {(["light", "dark"] as ThemeMode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cn("rounded px-3 py-1 text-sm", mode === m ? "bg-accent" : "")}
          >
            {t(m === "light" ? "theme.mode.light" : "theme.mode.dark")}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-2">
        {ROLE_NAMES.map((role: RoleName) => {
          const overridden = overrides[role];
          const value = overridden ?? generated[role];
          return (
            <div
              key={role}
              className={cn(
                "flex items-center gap-2 rounded-md px-1 py-0.5 text-sm",
                armedKey === `role:${mode}:${role}` && "ring-2 ring-primary",
              )}
              onClick={() => arm({ kind: "role", mode, role })}
            >
              <input
                type="color"
                value={value}
                onChange={(e) => setRoleOverride(mode, role, e.target.value)}
                onFocus={() => arm({ kind: "role", mode, role })}
                className="h-6 w-6 shrink-0 cursor-pointer rounded border bg-transparent"
                aria-label={humanize(role)}
              />
              <span className="flex-1 truncate">{humanize(role)}</span>
              {overridden && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setRoleOverride(mode, role, null); }}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={t("common.reset")}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
